import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IconButton, Tooltip } from "@mui/material";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { useVizDispatch } from "@/shared/state/visualization";
import { removeThreeDEntity } from "@/shared/state/visualization/actions";
import {
  descendantIds,
  type Entity,
  type EntityDeclaration,
  type EntityID,
  type EntityRegistry,
  type EntityTreeNode,
} from "@/shared/state/visualization/schema/v1/three-d";

import { DeleteEntityDialog } from "./DeleteEntityDialog";
import { EntityConfig } from "./EntityConfig";
import { EntityNameField } from "./EntityNameField";
import styles from "./EntityRow.module.css";

interface EntityRowProps {
  node: EntityTreeNode<EntityDeclaration>;
  /** Components by entity id. Sparse: a structural entity has none. */
  components: Record<EntityID, Entity>;
  registry: EntityRegistry;
  panelId: string;
  /** Ids to start expanded — a newly added entity, so its settings are to hand. */
  initiallyExpanded: Set<EntityID>;
  /** False when this is the panel's last entity; the schema requires one. */
  canDelete: boolean;
  /** Whether a model may be authored here. See {@link MeshConfig}. */
  modelMeshEnabled: boolean;
  /** Whether a point cloud may be authored here. See {@link EntityConfig}. */
  pointCloudEnabled: boolean;
}

/**
 * One entity in the tree: a collapsed header, or its full settings when opened.
 *
 * Collapsed by default beyond the first, because `EntityConfig` is around forty
 * form fields — a chain of five links expanded at once is unnavigable.
 *
 * Its children are the last thing inside the opened body, so a subtree belongs to
 * the entity that owns it — a hand-built hierarchy reads as one unit rather than
 * as a flat list. The cost is that reaching a leaf means opening each ancestor in
 * turn. An imported model contributes no children: one entity draws the file's
 * whole node hierarchy.
 *
 * The header is a flex row rather than a single button: a delete control nested
 * inside a disclosure button would be invalid markup and would fire both.
 */
export function EntityRow({
  node,
  components,
  registry,
  panelId,
  initiallyExpanded,
  canDelete,
  modelMeshEnabled,
  pointCloudEnabled,
}: EntityRowProps) {
  const dispatch = useVizDispatch();
  const [expanded, setExpanded] = React.useState(() =>
    initiallyExpanded.has(node.id),
  );
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const ChevronIcon = expanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon;

  const descendants = React.useMemo(
    () => [...descendantIds(registry, node.id)],
    [registry, node.id],
  );
  // The tree node carries only the declaration, so components are looked up by
  // id. A structural entity has none, which is why this can be absent.
  const entity: Entity = components[node.id] ?? { id: node.id };

  const remove = React.useCallback(
    (cascade: boolean) => {
      dispatch(removeThreeDEntity(panelId, node.id, cascade));
      setConfirmingDelete(false);
    },
    [dispatch, panelId, node.id],
  );

  const onDeleteClicked = React.useCallback(() => {
    // With no children there is only one possible outcome, so asking would be a
    // speed bump rather than a safeguard.
    if (descendants.length === 0) {
      remove(false);
    } else {
      setConfirmingDelete(true);
    }
  }, [descendants.length, remove]);

  return (
    <li className={styles.row}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={expanded}
          onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
        >
          <ChevronIcon className={styles.chevron} fontSize="inherit" />
          <span className={styles.name}>{node.id}</span>
        </button>
        <Tooltip
          title={
            canDelete
              ? "Delete entity"
              : "A panel must keep at least one entity"
          }
        >
          {/* A disabled IconButton fires no events, so the tooltip needs a
              wrapper to hang off. */}
          <span>
            <IconButton
              size="small"
              aria-label={`Delete ${node.id}`}
              disabled={!canDelete}
              onClick={onDeleteClicked}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      {expanded && (
        <div className={styles.body}>
          <EntityNameField
            entity={entity}
            registry={registry}
            panelId={panelId}
          />
          {/* No parent control: the hierarchy is still read, still saved and
              still composed — a stored `parent` keeps placing its entity — but
              it cannot be authored or edited from here. One consequence is worth
              knowing: an entity that already has a parent, including one naming
              an entity that no longer exists, cannot be re-aimed or detached
              from the sidebar, and a dangling link is no longer flagged. */}
          <EntityConfig
            entity={entity}
            panelId={panelId}
            modelMeshEnabled={modelMeshEnabled}
            pointCloudEnabled={pointCloudEnabled}
          />

          {/* Children are the entity's last section, after its components, so a
              subtree belongs to the entity that owns it rather than sitting
              beside it. */}
          {node.children.length > 0 && (
            <>
              <RobotoTypography
                variant="body2"
                className={styles.childrenLabel}
              >
                Children
              </RobotoTypography>
              <ul className={styles.children}>
                {node.children.map((child) => (
                  <EntityRow
                    key={child.id}
                    node={child}
                    components={components}
                    registry={registry}
                    panelId={panelId}
                    initiallyExpanded={initiallyExpanded}
                    canDelete={canDelete}
                    modelMeshEnabled={modelMeshEnabled}
                    pointCloudEnabled={pointCloudEnabled}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <DeleteEntityDialog
        open={confirmingDelete}
        entityId={node.id}
        descendants={descendants}
        promotedParent={registry[node.id]?.parent}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={remove}
      />
    </li>
  );
}
